import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, Users, ChartLineUp, CalendarDots, Crown, Plus,
  Play, ArrowLeft, GameController, X, Archive, CheckCircle
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LeagueDetailPage() {
  const { leagueId } = useParams();
  const { user } = useAuth();
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [showCreateSeason, setShowCreateSeason] = useState(false);
  const [seasonForm, setSeasonForm] = useState({ name: '', description: '', starts_at: '', ends_at: '', playoff_enabled: false });
  const navigate = useNavigate();

  const fetchLeague = () => {
    axios.get(`${API}/api/leagues/${leagueId}`, { withCredentials: true })
      .then(r => setLeague(r.data)).catch(e => console.error(e)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchLeague(); }, [leagueId]);

  const isOwner = league?.owner_user_id === user?.id;

  const handleJoin = async () => {
    setJoining(true);
    try {
      await axios.post(`${API}/api/leagues/${leagueId}/join`, {}, { withCredentials: true });
      toast.success('Joined league!');
      fetchLeague();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setJoining(false);
  };

  const handleCreateSeason = async () => {
    if (!seasonForm.name || !seasonForm.starts_at || !seasonForm.ends_at) { toast.error('Fill in all required fields'); return; }
    try {
      await axios.post(`${API}/api/leagues/${leagueId}/seasons`, seasonForm, { withCredentials: true });
      toast.success('Season created!');
      setShowCreateSeason(false);
      setSeasonForm({ name: '', description: '', starts_at: '', ends_at: '', playoff_enabled: false });
      fetchLeague();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSeasonAction = async (seasonId, action) => {
    try {
      await axios.put(`${API}/api/leagues/${leagueId}/seasons/${seasonId}`, { status: action }, { withCredentials: true });
      toast.success(`Season ${action}`);
      fetchLeague();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="max-w-4xl mx-auto"><div className="h-40 bg-white/[0.03] rounded-lg animate-pulse" /></div>;
  if (!league) return <p className="text-[#444] text-center mt-10">League not found</p>;

  const season = league.active_season;
  const standings = season?.standings || [];
  const TYPE_COLORS = { casual: '#10B981', competitive: '#EF4444', ladder: '#F59E0B', team: '#3B82F6', shop_league: '#9D4CDD' };
  const tc = TYPE_COLORS[league.league_type] || '#888';

  return (
    <div className="max-w-4xl mx-auto" data-testid="league-detail-page">
      <button onClick={() => navigate('/leagues')} className="flex items-center gap-1 text-[12px] text-[#555] hover:text-white mb-4 transition-colors">
        <ArrowLeft size={14} /> Back to Leagues
      </button>

      {/* Header */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center">
              <Trophy size={24} weight="duotone" className="text-[#F59E0B]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{league.name}</h1>
              <div className="flex items-center gap-2 mt-0.5 text-[12px] text-[#555]">
                <GameController size={13} /> {league.game_id}
                <span className="mx-1 text-[#222]">|</span>
                <span style={{ color: tc }}>{league.league_type?.replace(/_/g, ' ')}</span>
                <span className="mx-1 text-[#222]">|</span>
                <Users size={13} /> {league.member_count} members
              </div>
            </div>
          </div>
          <Button size="sm" className="h-8 text-[11px] bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold"
            onClick={handleJoin} disabled={joining} data-testid="join-league-btn">
            {joining ? 'Joining...' : 'Join League'}
          </Button>
        </div>
        {league.description && <p className="text-[12px] text-[#666] mt-3">{league.description}</p>}
        <div className="flex flex-wrap gap-2 mt-3 text-[10px]">
          <span className="px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] text-[#888]">Scoring: {league.scoring_system_type}</span>
          {league.scoring_rules && Object.entries(league.scoring_rules).map(([k, v]) => (
            <span key={k} className="px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] text-[#666]">
              {k.replace(/_/g, ' ')}: {v}
            </span>
          ))}
        </div>
      </div>

      {/* Season Management (Owner) */}
      {isOwner && (
        <div className="bg-[#0c0c14] border border-[#F59E0B]/15 rounded-xl p-5 mb-4" data-testid="season-management">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[14px] font-bold text-white flex items-center gap-2">
              <CalendarDots size={16} weight="duotone" className="text-[#F59E0B]" /> Season Management
            </h2>
            <Button size="sm" onClick={() => setShowCreateSeason(true)}
              className="h-7 text-[10px] bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold" data-testid="create-season-btn">
              <Plus size={12} className="mr-1" /> New Season
            </Button>
          </div>
          {league.seasons?.length > 0 ? (
            <div className="space-y-2">
              {league.seasons.map(s => (
                <div key={s.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]"
                  data-testid={`manage-season-${s.id}`}>
                  <div>
                    <span className="text-[13px] text-white font-medium">{s.name}</span>
                    <span className="text-[11px] text-[#555] ml-2">{s.starts_at} — {s.ends_at}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${
                      s.status === 'active' ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20' :
                      s.status === 'completed' ? 'bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20' :
                      s.status === 'archived' ? 'bg-white/5 text-[#555] border border-white/10' :
                      'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20'
                    }`}>{s.status}</span>
                    {s.status === 'draft' && (
                      <button onClick={() => handleSeasonAction(s.id, 'active')}
                        className="px-2 py-1 text-[10px] rounded bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 hover:bg-[#10B981]/20"
                        data-testid={`activate-season-${s.id}`}>Activate</button>
                    )}
                    {s.status === 'active' && (
                      <button onClick={() => handleSeasonAction(s.id, 'completed')}
                        className="px-2 py-1 text-[10px] rounded bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20 hover:bg-[#3B82F6]/20"
                        data-testid={`complete-season-${s.id}`}>Complete</button>
                    )}
                    {s.status === 'completed' && (
                      <button onClick={() => handleSeasonAction(s.id, 'archived')}
                        className="px-2 py-1 text-[10px] rounded bg-white/5 text-[#555] border border-white/10 hover:bg-white/10"
                        data-testid={`archive-season-${s.id}`}>Archive</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-[#444] text-center py-2">No seasons yet — create your first!</p>
          )}
        </div>
      )}

      {/* Active Season + Standings */}
      {season ? (
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4" data-testid="season-standings">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[15px] font-bold text-white flex items-center gap-2">
                <CalendarDots size={16} weight="duotone" className="text-[#9D4CDD]" /> {season.name}
              </h2>
              <p className="text-[11px] text-[#555]">{season.starts_at} — {season.ends_at}</p>
            </div>
            <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 font-medium">{season.status}</span>
          </div>
          {standings.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="standings-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#444] border-b border-white/[0.06]">
                    <th className="text-left py-2 px-2 w-10">#</th>
                    <th className="text-left py-2 px-2">Player</th>
                    <th className="text-center py-2 px-2">MP</th>
                    <th className="text-center py-2 px-2">W</th>
                    <th className="text-center py-2 px-2">L</th>
                    <th className="text-center py-2 px-2">D</th>
                    <th className="text-right py-2 px-2">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((s, i) => (
                    <tr key={s.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                      <td className="py-2.5 px-2"><span className={`text-[12px] font-bold ${i === 0 ? 'text-[#F59E0B]' : i === 1 ? 'text-[#C0C0C0]' : i === 2 ? 'text-[#CD7F32]' : 'text-[#555]'}`}>{s.rank_position || i + 1}</span></td>
                      <td className="py-2.5 px-2"><div className="flex items-center gap-1.5">{i === 0 && <Crown size={12} weight="fill" className="text-[#F59E0B]" />}<span className="text-[13px] text-white font-medium">{s.user_name}</span></div></td>
                      <td className="text-center py-2.5 px-2 text-[12px] text-[#888]">{s.matches_played}</td>
                      <td className="text-center py-2.5 px-2 text-[12px] text-[#10B981] font-medium">{s.wins}</td>
                      <td className="text-center py-2.5 px-2 text-[12px] text-[#EF4444]">{s.losses}</td>
                      <td className="text-center py-2.5 px-2 text-[12px] text-[#888]">{s.draws}</td>
                      <td className="text-right py-2.5 px-2 text-[13px] text-white font-bold">{s.points_earned}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (<p className="text-[12px] text-[#444] text-center py-4">No standings yet — play some matches!</p>)}
        </div>
      ) : (
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4 text-center">
          <CalendarDots size={28} className="mx-auto mb-2 text-[#333]" />
          <p className="text-[13px] text-[#555]">No active season</p>
        </div>
      )}

      {/* Members */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
        <h2 className="text-[14px] font-bold text-white mb-3">Members ({league.members?.length})</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {league.members?.map(m => (
            <div key={m.user_id} className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/[0.02] border border-white/[0.04]">
              <div className="w-7 h-7 rounded-full bg-[#F59E0B]/10 flex items-center justify-center text-[10px] text-[#F59E0B] font-bold">{m.name?.charAt(0) || '?'}</div>
              <div className="min-w-0"><p className="text-[12px] text-white font-medium truncate">{m.name}</p><p className="text-[10px] text-[#555]">@{m.username}</p></div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Season Modal */}
      <AnimatePresence>
        {showCreateSeason && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setShowCreateSeason(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-[#0c0c14] border border-white/[0.08] rounded-xl w-full max-w-md"
              onClick={e => e.stopPropagation()} data-testid="create-season-modal">
              <div className="border-b border-white/[0.06] px-5 py-3 flex items-center justify-between">
                <h2 className="text-[15px] font-bold text-white">Create Season</h2>
                <button onClick={() => setShowCreateSeason(false)} className="text-[#555] hover:text-white"><X size={16} /></button>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Season Name *</label>
                  <input value={seasonForm.name} onChange={e => setSeasonForm({ ...seasonForm, name: e.target.value })}
                    className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:border-[#F59E0B]/40 focus:outline-none"
                    placeholder="e.g., Summer 2026" data-testid="season-name-input" />
                </div>
                <div>
                  <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Description</label>
                  <input value={seasonForm.description} onChange={e => setSeasonForm({ ...seasonForm, description: e.target.value })}
                    className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:outline-none"
                    placeholder="Optional description" data-testid="season-desc-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Start Date *</label>
                    <input type="date" value={seasonForm.starts_at} onChange={e => setSeasonForm({ ...seasonForm, starts_at: e.target.value })}
                      className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:outline-none"
                      data-testid="season-start-input" />
                  </div>
                  <div>
                    <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">End Date *</label>
                    <input type="date" value={seasonForm.ends_at} onChange={e => setSeasonForm({ ...seasonForm, ends_at: e.target.value })}
                      className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:outline-none"
                      data-testid="season-end-input" />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={seasonForm.playoff_enabled} onChange={e => setSeasonForm({ ...seasonForm, playoff_enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-white/[0.2] bg-[#07070c]" />
                  <span className="text-[13px] text-white">Enable Playoffs</span>
                </label>
              </div>
              <div className="border-t border-white/[0.06] px-5 py-3 flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowCreateSeason(false)}
                  className="h-8 text-[11px] bg-transparent border-white/[0.08] text-[#888]">Cancel</Button>
                <Button size="sm" onClick={handleCreateSeason}
                  className="h-8 text-[11px] bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold"
                  disabled={!seasonForm.name || !seasonForm.starts_at || !seasonForm.ends_at}
                  data-testid="submit-season-btn">Create Season</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
